import { Image, Progress, Upload, type UploadFile } from 'antd';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import type { RcFile, UploadProgressEvent } from '../../../upload';
import type { Validation } from '../default-validations';

const { Dragger } = Upload;

export type ImageDraggerProperties<T extends { url: string }> = {
  alt?: string;
  fileList?: Array<UploadFile<T>>;
  loadImage: (file: Blob | File) => Promise<HTMLImageElement>;
  shape?: 'circle' | 'rectangle-stretch' | 'rectangle' | 'square';
  size?: 'large' | 'medium' | 'small';
  uploadFile: (
    file: Blob | RcFile | string,
    onProgress?: (progressEvent: UploadProgressEvent) => void,
  ) => Promise<T> | T;
  validations?: Validation[];
} & Omit<ComponentProps<typeof Dragger<T>>, 'fileList'>;

const ImageUpload = <T extends { url: string }>({
  alt,
  value,
}: {
  alt?: string;
  value: Exclude<Exclude<ImageDraggerProperties<T>['fileList'], undefined>[0], undefined>;
}) => {
  const { t } = useTranslation();
  const { percent, response, status, url } = value;
  if (typeof percent === 'number' && status === 'uploading') {
    return <Progress className="image-input__progress" percent={Math.min(percent, 99)} />;
  }

  return <Image alt={t(alt ?? 'Uploaded image')} preview={false} src={url ?? response?.url} />;
};

export default ImageUpload;
